import {
  ApiOutlined,
  ClockCircleOutlined,
  ControlOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  ExportOutlined,
  EyeOutlined,
  LinkOutlined,
  LockOutlined,
  LogoutOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { AppPaths } from "@p0security/shared/routes/constants";
import { message } from "antd";
import { ItemType } from "antd/lib/menu/interface";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { DOCS_URLS } from "constants/docsUrls";
import { Link, NavLink } from "react-router-dom";
import { spacing } from "styles/spacing";

import { getNavLinkClassName } from "../../../utils/activeNavLinkClassName";
import { UserRole } from "../../Login";
import { addSandboxMenu } from "../../Sandbox/Routes";
import {
  CONFIG_ROUTES_ROLES,
  ENVIRONMENT_ROLES,
  ENV_SETTINGS_ROLES,
  JIT_ROUTING_ROLES,
} from "../roles";

const newSpacer = (key: string): ItemType => ({
  key,
  type: "divider",
  style: { borderColor: "transparent", marginTop: `${spacing.scale.sm}px` },
});

export const newSidebarItems = (
  userAuthz: Set<UserRole>,
  isSandbox?: boolean,
  hasEnvironments?: boolean
): ItemType[] => {
  const items: ItemType[] = [];

  if (ENVIRONMENT_ROLES.some((r) => userAuthz.has(r))) {
    items.push(
      {
        key: AppPaths.Dashboard,
        icon: <DashboardOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Dashboard}>
            Dashboard
          </LinkWithEnvironment>
        ),
      },
      {
        key: AppPaths.AI,
        icon: <RobotOutlined />,
        label: <LinkWithEnvironment to={AppPaths.AI}>AI</LinkWithEnvironment>,
      },
      {
        key: AppPaths.Inventory,
        icon: <DatabaseOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Inventory}>
            Inventory
          </LinkWithEnvironment>
        ),
      },
      {
        key: AppPaths.Posture,
        icon: <EyeOutlined />,
        label: (
          <LinkWithEnvironment to={AppPaths.Posture}>
            Posture
          </LinkWithEnvironment>
        ),
      }
    );
  }

  if (isSandbox) {
    addSandboxMenu(items, userAuthz.has("owner"));
  }

  items.push({
    key: AppPaths.AccessManagement,
    icon: <ClockCircleOutlined />,
    label: (
      <NavLink to={AppPaths.AccessManagement} className={getNavLinkClassName}>
        Access Management
      </NavLink>
    ),
  });

  if (JIT_ROUTING_ROLES.some((r) => userAuthz.has(r))) {
    items.push({
      key: AppPaths.Policies,
      icon: <SafetyCertificateOutlined />,
      label: (
        <NavLink to={AppPaths.Policies} className={getNavLinkClassName}>
          Policy Studio
        </NavLink>
      ),
    });
    items.push({
      key: AppPaths.Vault,
      icon: <LockOutlined />,
      label: (
        <NavLink to={AppPaths.Vault} className={getNavLinkClassName}>
          Vault
        </NavLink>
      ),
    });
  }

  if (hasEnvironments && ENV_SETTINGS_ROLES.some((r) => userAuthz.has(r))) {
    items.push({
      key: AppPaths.Settings,
      icon: <SettingOutlined />,
      label: (
        <LinkWithEnvironment to={AppPaths.Settings}>
          Settings
        </LinkWithEnvironment>
      ),
    });
  }

  if (CONFIG_ROUTES_ROLES.some((r) => userAuthz.has(r))) {
    items.push(newSpacer("spacer-admin"), {
      key: AppPaths.Integrations,
      icon: <ApiOutlined />,
      label: (
        <NavLink to={AppPaths.Integrations} className={getNavLinkClassName}>
          Integrations
        </NavLink>
      ),
    });
  }

  if (userAuthz.has("owner")) {
    items.push({
      key: AppPaths.P0Admin,
      icon: <ControlOutlined />,
      label: (
        <NavLink to={AppPaths.P0Admin} className={getNavLinkClassName}>
          P0 Management
        </NavLink>
      ),
    });
  }

  items.push(newSpacer("spacer-docs"));

  items.push({
    key: "docs",
    icon: <LinkOutlined />,
    label: (
      <Link to={DOCS_URLS.BASE} target="_blank" rel="noreferrer">
        Documentation <ExportOutlined style={{ marginLeft: 4 }} />
      </Link>
    ),
  });

  return items;
};

export const newBottomItems = (signOut: () => Promise<void>): ItemType[] => [
  {
    key: "logout",
    icon: <LogoutOutlined />,
    label: "Logout",
    title: "Logout",
    danger: true,
    onClick: async () => {
      try {
        await signOut();
      } catch {
        message.error("Failed to sign out. Please try again.");
      }
    },
  },
];
